import { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SectionTitle from '@/components/General/SectionTitle';
import Unicon from '@/components/General/Unicon';
import SortableList from '@/components/General/SortableList';
import { createShortcut, deleteShortcut, reorderShortcuts } from '@/data/shortcuts';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Query } from '@/utils/react-query/Queries';
import useShortcutAccess from '@/utils/hooks/useShortcutAccess';
import Toast from 'react-native-toast-message';

// Module key -> theme color key (see themes.js module colors)
const MODULE_COLOR_KEY = {
    'People': 'people',
    'Events': 'events',
    'Tasks': 'tasks',
    'Roles': 'admin',
    'Training': 'training',
    'Automations': 'paths',
    'Paths': 'paths', // legacy stored module name
    'Follow Up': 'paths', // rides the shared 'paths' module color
    'Small Groups': 'groups',
    'Giving': 'giving',
    'Connect': 'connect',
    'Analytics': 'analytics',
    'Admin': 'admin',
    'Other': 'primary',
};

// Infer module from a screen path (for existing shortcuts)
const inferModuleFromScreen = (screen) => {
    if (!screen) return 'Other';
    const seg = screen.split('/')[1];
    if (seg === 'people') return 'People';
    if (seg === 'events') return 'Events';
    if (seg === 'tasks') return 'Tasks';
    if (seg === 'roles' || seg === 'permissions') return 'Roles';
    if (seg === 'training') return 'Training';
    if (seg === 'automations' || seg === 'paths') return 'Automations';
    if (seg === 'follow-up') return 'Follow Up';
    if (seg === 'smallgroups') return 'Small Groups';
    if (seg === 'connect') return 'Connect';
    if (seg === 'contributions') return 'Giving';
    if (seg === 'analytics') return 'Analytics';
    if (seg === 'customfields' || seg === 'registrations') return 'Admin';
    return 'Other';
};

// Static presets — module field drives grouping + coloring
const STATIC_PRESETS = [
    { screen: '/people', label: 'People', icon: 'people-outline', module: 'People' },
    { screen: '/people/create', label: 'New Person', icon: 'person-add-outline', module: 'People' },
    { screen: '/events', label: 'Events', icon: 'calendar-outline', module: 'Events' },
    { screen: '/events/create', label: 'New Event', icon: 'calendar-plus', library: 'fa', module: 'Events' },
    { screen: '/events/blackouts/create', label: 'Add Blackout Date', icon: 'calendar-xmark', library: 'fa', module: 'Events' },
    { screen: '/tasks', label: 'Tasks', icon: 'clipboard-text', library: 'material', module: 'Tasks' },
    { screen: '/tasks/create', label: 'New Task', icon: 'clipboard-plus', library: 'material', module: 'Tasks' },
    { screen: '/smallgroups', label: 'Small Groups', icon: 'book-open-variant', library: 'material', module: 'Small Groups' },
    { screen: '/smallgroups/create', label: 'New Small Group', icon: 'book-plus', library: 'material', module: 'Small Groups' },
    { screen: '/smallgroups/sessions/create', label: 'Log Small Group Session', icon: 'people-outline', module: 'Small Groups' },
    { screen: '/contributions', label: 'Contributions', icon: 'cash-outline', module: 'Giving' },
    { screen: '/connect/boards/feed', label: 'My Feed', icon: 'newspaper-outline', module: 'Connect' },
    { screen: '/analytics', label: 'Analytics', icon: 'bar-chart-outline', module: 'Analytics' },
    { screen: '/roles', label: 'Roles & Permissions', icon: 'shield-outline', module: 'Roles' },
    { screen: '/registrations', label: 'New Registrations', icon: 'person-add-outline', module: 'Admin' },
    { screen: '/customfields', label: 'Custom Fields', icon: 'options-outline', module: 'Admin' },
    { screen: '/smallgroups/types', label: 'Small Group Types', icon: 'book-outline', module: 'Admin' },
];

// Module display order for grouping
const MODULE_ORDER = ['People', 'Events', 'Tasks', 'Small Groups', 'Training', 'Automations', 'Follow Up', 'Roles', 'Giving', 'Connect', 'Analytics', 'Admin', 'Other'];

const ShortcutsManagerModal = ({ isOpen, onClose, personID, existingShortcuts = [] }) => {
    const [isReordering, setIsReordering] = useState(false);
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();
    const { theme: { colors } } = useUnistyles();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: smallGroupTypes = [] } = Query.smallGroupTypes.all({});
    const canAccess = useShortcutAccess();

    const existingScreens = existingShortcuts.map(s => s.screen);
    // Hide (never delete) saved shortcuts the user lost access to — they may
    // regain permission, and hidden rows still dedupe presets by screen.
    const visibleExistingShortcuts = existingShortcuts.filter(s => canAccess(s.screen));

    // Build dynamic presets: My Profile + per-type New <Type> + New <Type> Session
    const dynamicPresets = useMemo(() => {
        const presets = [];

        // My Profile + own-profile tab deep links
        if (loggedInUser?.id) {
            presets.push({
                screen: `/people/${loggedInUser.id}`,
                label: 'My Profile',
                icon: 'person-outline',
                module: 'People',
            });
            presets.push({
                screen: `/people/${loggedInUser.id}?tab=giving`,
                label: 'My Giving',
                icon: 'cash-outline',
                module: 'Giving',
            });
            presets.push({
                screen: `/people/${loggedInUser.id}?tab=tasks`,
                label: 'My Tasks',
                icon: 'clipboard-text',
                library: 'material',
                module: 'Tasks',
            });
            presets.push({
                screen: `/people/${loggedInUser.id}?tab=training`,
                label: 'My Training',
                icon: 'school-outline',
                module: 'Training',
            });
        }

        // Per small group type
        smallGroupTypes.forEach((type) => {
            const typeName = type.name || 'Small Group';
            presets.push({
                screen: `/smallgroups/create?typeID=${type.id}`,
                label: `New ${typeName}`,
                icon: 'book-plus',
                library: 'material',
                module: 'Small Groups',
            });
            presets.push({
                screen: `/smallgroups/sessions/create?typeID=${type.id}`,
                label: `New ${typeName} Session`,
                icon: 'people-outline',
                module: 'Small Groups',
            });
        });

        return presets;
    }, [loggedInUser?.id, smallGroupTypes]);

    // All available presets, filtered to those not already added
    const availablePresets = useMemo(() => {
        const all = [...dynamicPresets, ...STATIC_PRESETS];
        // Dedupe by screen, keeping first occurrence (dynamic first so type-specific wins over generic)
        const seen = new Set();
        const deduped = [];
        for (const p of all) {
            if (seen.has(p.screen)) continue;
            seen.add(p.screen);
            if (existingScreens.includes(p.screen)) continue;
            if (!canAccess(p.screen)) continue; // never offer presets the user can't open
            deduped.push(p);
        }
        return deduped;
    }, [dynamicPresets, existingScreens, canAccess]);

    // Group available presets by module
    const groupedPresets = useMemo(() => {
        const groups = {};
        availablePresets.forEach((p) => {
            const mod = p.module || 'Other';
            if (!groups[mod]) groups[mod] = [];
            groups[mod].push(p);
        });
        // Return ordered list of [module, presets]
        return MODULE_ORDER
            .filter((m) => groups[m]?.length > 0)
            .map((m) => [m, groups[m]]);
    }, [availablePresets]);

    const getModuleColors = (moduleKey) => {
        const colorKey = MODULE_COLOR_KEY[moduleKey] || 'primary';
        return {
            color: colors[colorKey],
            container: colors[`${colorKey}Container`] || colors.primaryContainer,
        };
    };

    // Infer module + colors for an existing shortcut
    const getShortcutColors = (shortcut) => {
        const mod = shortcut.parameters?.module || inferModuleFromScreen(shortcut.screen);
        return getModuleColors(mod);
    };

    const handleAddShortcut = async (shortcutData) => {
        setLoading(true);
        try {
            const newShortcut = await createShortcut({
                personID,
                screen: shortcutData.screen,
                label: shortcutData.label,
                icon: shortcutData.icon,
                library: shortcutData.library,
                parameters: shortcutData.module ? { module: shortcutData.module } : null,
            });
            queryClient.setQueryData(
                QueryKeys.shortcuts.byPersonId(personID),
                (old) => old ? [...old, newShortcut] : [newShortcut]
            );
            queryClient.setQueryData(
                QueryKeys.dashboardData.byPersonId(personID),
                (old) => old ? { ...old, shortcuts: [...(old.shortcuts || []), newShortcut] } : old
            );
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to add shortcut' });
        }
        setLoading(false);
    };

    const handleDeleteShortcut = async (shortcutId) => {
        setLoading(true);
        try {
            await deleteShortcut(shortcutId);
            queryClient.setQueryData(
                QueryKeys.shortcuts.byPersonId(personID),
                (old) => old ? old.filter(s => s.id !== shortcutId) : []
            );
            queryClient.setQueryData(
                QueryKeys.dashboardData.byPersonId(personID),
                (old) => old ? { ...old, shortcuts: (old.shortcuts || []).filter(s => s.id !== shortcutId) } : old
            );
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to remove shortcut' });
        }
        setLoading(false);
    };

    const handleReorderConfirm = async (reorderedItems) => {
        setLoading(true);
        try {
            const orderedIds = reorderedItems.map(item => item.id);
            await reorderShortcuts(personID, orderedIds);
            queryClient.setQueryData(
                QueryKeys.shortcuts.byPersonId(personID),
                (old) => {
                    if (!old) return old;
                    const itemMap = new Map(old.map(item => [item.id, item]));
                    return orderedIds.map(id => itemMap.get(id)).filter(Boolean);
                }
            );
            queryClient.setQueryData(
                QueryKeys.dashboardData.byPersonId(personID),
                (old) => {
                    if (!old?.shortcuts) return old;
                    const itemMap = new Map(old.shortcuts.map(item => [item.id, item]));
                    return { ...old, shortcuts: orderedIds.map(id => itemMap.get(id)).filter(Boolean) };
                }
            );
            Toast.show({ type: 'success', text2: 'Shortcuts reordered' });
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to reorder shortcuts' });
        }
        setLoading(false);
    };

    const renderShortcutItem = (shortcut) => {
        const { color, container } = getShortcutColors(shortcut);
        return (
            <View style={styles.sortableItemContent}>
                <View style={[styles.iconContainer, { backgroundColor: container }]}>
                    <Unicon
                        name={shortcut.parameters?.icon || 'link-outline'}
                        size={18}
                        style={{ color }}
                        library={shortcut.parameters?.library || 'ionicon'}
                    />
                </View>
                <View style={styles.shortcutTextContainer}>
                    <Text style={styles.shortcutLabel}>
                        {shortcut.parameters?.label || shortcut.screen}
                    </Text>
                </View>
            </View>
        );
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title="Manage Shortcuts"
            cancelText="Done"
            loading={loading}
        >
            <ScrollView
                contentContainerStyle={styles.scrollContainer}
                showsVerticalScrollIndicator={true}
                keyboardShouldPersistTaps="handled"
            >
                {visibleExistingShortcuts.length > 0 && (
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <SectionTitle title="Your Shortcuts" marginLeft={0} marginTop={0} />
                            {visibleExistingShortcuts.length > 1 && (
                                <TouchableOpacity
                                    onPress={() => setIsReordering(true)}
                                    style={styles.reorderButton}
                                >
                                    <Unicon name="reorder-four-outline" size={18} colorVariant="primary" />
                                    <Text style={styles.reorderButtonText}>Reorder</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                        {visibleExistingShortcuts.map((shortcut) => {
                            const { color, container } = getShortcutColors(shortcut);
                            return (
                                <View key={shortcut.id} style={styles.shortcutRow}>
                                    <View style={styles.shortcutInfo}>
                                        <View style={[styles.iconContainer, { backgroundColor: container }]}>
                                            <Unicon
                                                name={shortcut.parameters?.icon || 'link-outline'}
                                                size={18}
                                                style={{ color }}
                                                library={shortcut.parameters?.library || 'ionicon'}
                                            />
                                        </View>
                                        <View style={styles.shortcutTextContainer}>
                                            <Text style={styles.shortcutLabel}>
                                                {shortcut.parameters?.label || shortcut.screen}
                                            </Text>
                                            <Text style={styles.shortcutScreen}>{shortcut.screen}</Text>
                                        </View>
                                    </View>
                                    <TouchableOpacity
                                        onPress={() => handleDeleteShortcut(shortcut.id)}
                                        style={styles.deleteButton}
                                    >
                                        <Unicon name="trash-outline" size={18} colorVariant="error" />
                                    </TouchableOpacity>
                                </View>
                            );
                        })}
                    </View>
                )}

                <SortableList
                    items={existingShortcuts}
                    itemContent={renderShortcutItem}
                    isOpen={isReordering}
                    close={() => setIsReordering(false)}
                    onConfirm={handleReorderConfirm}
                    title="Reorder Shortcuts"
                    itemHeight={50}
                    helpText="Drag to reorder your shortcuts"
                />

                <View style={styles.section}>
                    <SectionTitle title="Add Shortcuts" marginLeft={0} marginTop={0} />
                    <Text style={styles.helpText}>Tap to add a quick link to your dashboard</Text>

                    {groupedPresets.map(([moduleKey, presets]) => {
                        const { color, container } = getModuleColors(moduleKey);
                        return (
                            <View key={moduleKey} style={styles.moduleGroup}>
                                <Text style={styles.moduleLabel}>{moduleKey}</Text>
                                <View style={styles.availableGrid}>
                                    {presets.map((shortcut) => (
                                        <TouchableOpacity
                                            key={shortcut.screen}
                                            style={styles.availableItem}
                                            onPress={() => handleAddShortcut(shortcut)}
                                        >
                                            <View style={[styles.availableIconContainer, { backgroundColor: container }]}>
                                                <Unicon
                                                    name={shortcut.icon}
                                                    size={20}
                                                    style={{ color }}
                                                    library={shortcut.library}
                                                />
                                            </View>
                                            <Text style={styles.availableLabel}>{shortcut.label}</Text>
                                        </TouchableOpacity>
                                    ))}
                                </View>
                            </View>
                        );
                    })}
                </View>

            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    scrollContainer: {
        paddingBottom: 50,
    },
    section: {
        marginBottom: 24,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    reorderButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    reorderButtonText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary,
    },
    sortableItemContent: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    helpText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginBottom: 12,
    },
    shortcutRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 10,
        paddingHorizontal: 12,
        backgroundColor: colors.surface,
        borderRadius: 8,
        marginBottom: 8,
    },
    shortcutInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    iconContainer: {
        width: 32,
        height: 32,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    shortcutTextContainer: {
        flex: 1,
    },
    shortcutLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
    },
    shortcutScreen: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    deleteButton: {
        padding: 8,
    },
    moduleGroup: {
        marginBottom: 16,
    },
    moduleLabel: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
        marginTop: 4,
    },
    availableGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    availableItem: {
        alignItems: 'center',
        padding: 12,
        borderRadius: 8,
        backgroundColor: colors.surface,
        flexBasis: '31%',
        flexGrow: 0,
        flexShrink: 0,
        _web: {
            transition: "background-color 0.15s ease, transform 0.15s ease",
            cursor: "pointer",
            _hover: { backgroundColor: colors.hover },
        },
    },
    availableIconContainer: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 6,
    },
    availableLabel: {
        fontSize: 10,
        fontFamily: fonts.body,
        color: colors.text,
        textAlign: 'center',
    },
}));

export default ShortcutsManagerModal;
